import { resolveMetadataPointers } from './resolveMetadataPointers';

/**
 * Enriches defunto metadata for document templates (Mustache).
 * Resolves all pointer fields to full objects with accessible properties.
 *
 * Metadata is already fetched via GraphQL.
 * This function resolves pointer IDs to full objects for template access.
 *
 * @param {Object} metadata - The raw metadata object from defunto.metadata
 * @returns {Object} Enriched metadata with resolved pointers
 *
 * @example
 * // Input metadata:
 * {
 *   cimitero_ceneri: { type: "pointer", objectId: "abc123", className: "cimiteri" },
 *   rito: { type: "string", value: "Cattolico" }
 * }
 *
 * // Output:
 * {
 *   cimitero_ceneri: { objectId: "abc123", nome: "Cimitero Centrale", citta: "Milano", ... },
 *   rito: "Cattolico"
 * }
 *
 * // Template access:
 * {{s.metadata.cimitero_ceneri.nome}}  -> "Cimitero Centrale"
 * {{s.metadata.rito}}                  -> "Cattolico"
 */
export async function getEnrichedContextDefuntoMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return {};

  const resolved = await resolveMetadataPointers(metadata);
  const enriched = {};

  for (const [key, entry] of Object.entries(resolved)) {
    if (entry?._resolved) {
      // Pointer field - expose full object for template access
      // {{s.metadata.cimitero_ceneri.nome}}, {{s.metadata.cimitero_ceneri.citta}}
      enriched[key] = entry._resolved;
    } else if (entry?.type) {
      // Typed entries - handle each type appropriately
      // Check type BEFORE value to ensure date-timestamp gets converted
      switch (entry.type) {
        case 'boolean':
          enriched[key] = String(entry.value) === 'true';
          break;
        case 'date-timestamp':
          enriched[key] = entry.value
            ? new Date(Number(entry.value)).toISOString()
            : null;
          break;
        case 'float':
          enriched[key] = Number(entry.value);
          break;
        case 'array':
        case 'object':
          enriched[key] = entry.value;
          break;
        case 'string':
        default:
          // String and other types - expose value directly
          // {{s.metadata.rito}} instead of {{s.metadata.rito.value}}
          enriched[key] = entry.value;
      }
    } else if (entry?.value !== undefined) {
      // Fallback for untyped entries with value property
      enriched[key] = entry.value;
    } else {
      // Fallback for completely untyped entries
      enriched[key] = entry;
    }
  }

  return enriched;
}
